/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
import { AnyAction, Reducer } from 'redux';
import { put, takeEvery } from 'redux-saga/effects';

import { ILooseObject } from '@interface/loose-object';
import { ISagaEvent } from '@interface/saga-event';
import { GetTokenAuctions, GetTokenHistory, GetTokenHolders, GetTokenOffers, GetTokenSwaps } from '@lib/versum';
import { isActionActive } from '@utils/auctions';
import { didExpire } from '@utils/market';

export interface IToken {
  loading: boolean;
  id: string | null;
  creators: any[]; // array with creators
  swaps: any[]; // array with all swaps
  swap: any | null; // object with the lowest swap
  creator_swap: any | null; // creator lowest swap. Used to prioritise creator swaps vs other swaps
  auctions: any[]; // array with all auctions
  auction: any | null; // object with the auction ending earlier
  offers: any[]; // array with all offers
  holders: any[]; // array with token holders (collectors)
  history: any[]; // array with token sales history
}

export const initialState: IToken = {
  loading: true,
  id: null,
  creators: [],
  swaps: [],
  swap: null,
  creator_swap: null,
  auctions: [],
  auction: null,
  offers: [],
  holders: [],
  history: [],
};

export enum actions {
  TOKEN_UPDATE_TOKEN = 'TOKEN_UPDATE_TOKEN', // a new token comes in (saga)
  TOKEN_UPDATE = 'TOKEN_UPDATE', // update current token (internal)
}

export const reducer: Reducer<IToken, AnyAction> = (state = initialState, action) => {
  switch (action.type) {
    case actions.TOKEN_UPDATE:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

function* updateCurrentToken(event: ISagaEvent) {
  yield put({
    type: actions.TOKEN_UPDATE,
    payload: {
      loading: true,
      id: null,
      creators: [],
      swaps: [],
      swap: null,
      creator_swap: null,
      auctions: [],
      auction: null,
      offers: [],
      holders: [],
      history: [],
    },
  });

  const { market_swaps }: ILooseObject = yield GetTokenSwaps(event.payload.id);
  const { market_auctions }: ILooseObject = yield GetTokenAuctions(event.payload.id);
  const { market_offers }: ILooseObject = yield GetTokenOffers(event.payload.id);
  const { token_holders }: ILooseObject = yield GetTokenHolders(event.payload.id);
  const { token_price_events }: ILooseObject = yield GetTokenHistory(event.payload.id);

  const getLowestSwap = () => {
    const sorted: any[] = market_swaps
      // order by price
      .sort((a: any, b: any) => {
        return a.current_price > b.current_price ? 1 : -1;
      })
      // dont display expired swaps
      .filter((s: any) => {
        return !didExpire(s.ending_time);
      });

    return sorted.length > 0 ? sorted[0] : false;
  };

  const getCreatorLowestSwap = () => {
    // sort swaps by price
    const sorted: any[] = market_swaps.sort((a: any, b: any) => {
      return a.current_price > b.current_price ? 1 : -1;
    });

    // goes through all users on the swaps
    const user = sorted.filter((e) => {
      // check against the creators array
      const found = e.seller.user.find((u: any) => {
        const swapCreatorId = u.user.id;
        const isSwapCreatorATokenCreator = event.payload.creators.filter(
          (c: any) => !!(c.wallet.address === swapCreatorId)
        );

        return isSwapCreatorATokenCreator.length > 0;
      });

      if (found) {
        return e;
      }

      return false;
    });

    return user.length > 0 ? user[0] : null;
  };

  const getQuickerAuction = () => {
    /*
    This is the auction to display on the main page detail.
    If the auction ends, we should show the next auction.
    If there's no more auctions we show swaps
    If no swaps we show Offers.

    */
    // README:
    // the quicker auction is an auction that hasn't terminated yet.
    // if an auction ended but the `withdraw` method hasn't been called
    // the status will not change to CONCLUDED and therefore will not be excluded on the
    // graphql request, ending up showing as the current active auction.
    // we're filtering it to figure out here so its excluded from the detail page
    // but is available on the table under the "Market" to display the claim button.
    const sorted: any[] = market_auctions.filter(isActionActive).sort((a: any, b: any) => {
      return a.end_time > b.end_time ? 1 : -1;
    });

    return sorted.length > 0 ? sorted[0] : false;
  };

  yield put({
    type: actions.TOKEN_UPDATE,
    payload: {
      loading: false,
      id: event.payload.id,
      creators: event.payload.creators,
      swaps: market_swaps,
      auctions: market_auctions,
      offers: market_offers,
      swap: getLowestSwap(),
      creator_swap: getCreatorLowestSwap(),
      auction: getQuickerAuction(),
      holders: token_holders,
      history: token_price_events,
    },
  });
}

export function* saga() {
  yield takeEvery(actions.TOKEN_UPDATE_TOKEN, updateCurrentToken);
}
